import React, { Fragment, lazy, Suspense, useRef, useState } from 'react';
import tw from "twin.macro";
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '@selfbook/atoms-v2';
import {
  useDisplayDates,
  useDisplayRooms,
  usePopoutDirection,
} from '../../hooks';
import { intl } from '../../lang/INTL';
import useWidgetStore from '../../store/useWidgetStore';
import { selectColorTheme, selectIsGroupOfHotels, selectSelectedHotel } from '../../store/selectors';
import { ButtonStyled, SelectorButtonStyled } from '../../styles/common.styled';
import {
  Row,
  TriggerDiv,
  TriggerEllipsis,
  TriggerIcon,
  Wrapper,
} from './Widget.styled';

const CalendarModule = lazy(() => import('../CalendarModule/CalendarModule.jsx').then((m) => ({ default: m.CalendarModule })));
const Destination = lazy(() => import('../Destination/Destination.jsx').then((m) => ({ default: m.Destination })));
const Guests = lazy(() => import('../Guests/Guests.jsx').then((m) => ({ default: m.Guests })));
const Promo = lazy(() => import('../Promo/Promo.jsx').then((m) => ({ default: m.Promo })));
const Submit = lazy(() => import('../Submit/Submit.jsx').then((m) => ({ default: m.Submit })));

const motionProps = {
  initial: { opacity: 0 },
  exit: { opacity: 0 },
  animate: { opacity: 1 }
};

const Trigger = ({ className, children, iconLeft, iconRight, label, ...rest }) => {
  const triggersConfig = useWidgetStore((state) => state.config?.triggers);
  const theme = useWidgetStore((state) => state.theme);
  const colorTheme = useWidgetStore(selectColorTheme);
  const { shape, showIconLeft, showIconRight, type } = triggersConfig || {};

  const commonProps = {
    className: ['btn-trigger', className].join(' '),
    colorTheme,
    theme,
    "$flex": 'none',
    variant: 'ghost',
  };

  return type === 'labeled' ? (
    <SelectorButtonStyled
      {...commonProps}
      label={label}
      value={children}
      {...rest}
    />
  ) : (
    <ButtonStyled
      {...commonProps}
      shape={shape === 'square' ? 'square' : 'pill'}
      {...rest}
    >
      {!!iconLeft && !!showIconLeft && <TriggerIcon name={iconLeft} $theme={theme} />}
      {children}
      {!!iconRight && !!showIconRight && <TriggerIcon name={iconRight} $theme={theme} key={iconRight} />}
    </ButtonStyled>
  );
};

export const Widget = ({ config }) => {
  const [openModule, setOpenModule] = useState(null); // destination, calendar, guests, promo
  const widgetRef = useRef(null);
  usePopoutDirection(widgetRef, !!openModule); // Determines viewport position
  const theme = useWidgetStore((state) => state.theme);
  const colorTheme = useWidgetStore(selectColorTheme);
  const hotelData = useWidgetStore((state) => state.hotelData);
  const isGroupOfHotels = useWidgetStore(selectIsGroupOfHotels);
  const selectedHotel = useWidgetStore(selectSelectedHotel);
  const datesSelected = useWidgetStore((state) => !!state.calendar?.start_date && !!state.calendar?.end_date);
  const hotelPreselected = useWidgetStore((state) => state.config?.hotelPreselected);
  const triggerConfig = useWidgetStore((state) => state.config?.triggers);
  const { triggerText: calendarText, hasDates } = useDisplayDates();
  const { triggerText: roomsText } = useDisplayRooms();

  const getIconRight = (key) => {
    return key === openModule ? 'chevron-up' : 'chevron-down';
  };

  const handleToggleModule = (key) => {
    setOpenModule((prev) => key === prev ? null : key);
  };

  return (
    <Wrapper
      ref={widgetRef}
      className="widget-main desktop-only"
      $colorTheme={colorTheme}
      $theme={theme}
    > 
      <Row $justify="between" className="widget--inner">
        <Row $padLeft={isGroupOfHotels === null} $flex="grow" className="trigger-row">
          {triggerConfig?.order?.map((key) => (
            <Fragment key={key}>
              {key === 'destination' && (
                <Trigger
                  className="btn-trigger--destination"
                  iconLeft="location"
                  iconRight={isGroupOfHotels && getIconRight('destination')}
                  isSkeleton={!hotelData || (hotelPreselected && !selectedHotel?.name)}
                  label={triggerConfig?.labels?.destination}
                  rightIcon={triggerConfig?.type === 'labeled' ? 'add' : undefined}
                  onClick={() => isGroupOfHotels ? handleToggleModule('destination') : null}
                  $active={openModule === 'destination'}
                  $flex="shrink"
                  $overflowText
                >
                  <TriggerEllipsis className={`trigger__btn-text trigger__btn-text--destination trigger__btn-text--destination--${selectedHotel?.name ? 'selected' : 'unselected'}`}>
                    {selectedHotel?.name
                      || intl.formatMessage({
                        id: 'destinations.selectDestination',
                        defaultMessage: 'Select Destination',
                      })}
                  </TriggerEllipsis>
                </Trigger>
              )}
              {key === 'calendar' && (
                <Trigger
                  className="btn-trigger--calendar"
                  iconLeft="calendar"
                  iconRight={getIconRight('calendar')}
                  label={triggerConfig?.labels?.calendar}
                  onClick={() => handleToggleModule('calendar')}
                  $active={openModule === 'calendar'}
                >
                  <div className={`trigger__btn-text trigger__btn-text--calendar trigger__btn-text--calendar--${hasDates ? 'selected' : 'unselected'}`}>
                    {calendarText}
                  </div>
                </Trigger>
              )}
              {key === 'guests' && (
                <TriggerDiv className="trigger-div trigger-div--guests">
                  <Trigger
                    className="btn-trigger--guests"
                    iconLeft="user-multiple"
                    iconRight={getIconRight('guests')}
                    isSkeleton={!roomsText}
                    label={triggerConfig?.labels?.guests}
                    onClick={() => handleToggleModule('guests')}
                    $active={openModule === 'guests'}
                  >
                    <div className="trigger__btn-text trigger__btn-text--guests">{roomsText || '\u00A0'}</div>
                  </Trigger>
                  <Suspense fallback={null}>
                    <AnimatePresence>
                      {openModule === 'guests' && (
                        <motion.div {...motionProps} key="guests">
                          <Guests onClose={() => setOpenModule(null)}/>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Suspense>
                </TriggerDiv>
              )}
              {key === 'promo' && (
                <TriggerDiv className="trigger-div trigger-div--promo">
                  <Trigger
                    className="btn-trigger--promo"
                    iconLeft="gift"
                    iconRight={getIconRight('promo')}
                    label={triggerConfig?.labels?.promo}
                    disabled={!selectedHotel || !datesSelected}
                    onClick={() => handleToggleModule('promo')}
                    $active={openModule === 'promo'}
                  >
                    <div className="trigger__btn-text trigger__btn-text--promo">
                      {intl.formatMessage({
                        id: 'promo.addCode',
                        defaultMessage: 'Add Code',
                      })}
                    </div>
                  </Trigger>
                  <Suspense fallback={null}>
                    <AnimatePresence>
                      {openModule === 'promo' && (
                        <motion.div {...motionProps} key="promo">
                          <Promo onClose={() => setOpenModule(null)}/>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Suspense>
                </TriggerDiv>
              )}
            </Fragment>
          ))}
        </Row>
        <Submit />
      </Row>
      <Suspense fallback={null}>
        <AnimatePresence>
          {openModule === 'destination' && (
            <motion.div {...motionProps} key="destination">
              <Destination onClose={() => setOpenModule(null)}/>
            </motion.div>
          )}
        </AnimatePresence>
      </Suspense>
      <Suspense fallback={null}>
        <AnimatePresence>
          {openModule === 'calendar' && (
            <motion.div {...motionProps} key="calendar">
              <CalendarModule onClose={() => setOpenModule(null)}/>
            </motion.div>
          )}
        </AnimatePresence>
      </Suspense>
    </Wrapper>
  );
}
